// ── useMarketData.js ───────────────────────────────────────
// Fetches live data for every ticker in the portfolio and
// merges it into the holdings. Components just read
// `enrichedHoldings` instead of raw holdings.
//
// Two sources:
// 1) Alpha Vantage (requires REACT_APP_ALPHA_KEY) — overview, P/E, P/B,
//    EV/EBITDA, beta, analyst target, sector, etc. 25 req/day free.
// 2) Yahoo quoteSummary (always) — live fundamentals (3 at a time, 30 min cache)
//    Yahoo overwrites stale stocks.js fundamentals within a few seconds.
//
// NOTE: Alpha Vantage free tier is 25 req/day & 5 req/min.
// For large portfolios (10+ stocks) Yahoo fundamentals will cover most data.
// Alpha is used for enrichment (analyst targets, descriptions, etc.)

import { useState, useEffect, useCallback, useRef } from "react";
import { fetchMultipleStocks } from "../services/marketData";
import { fetchFundamentalsBatch, clearFundamentalsCache } from "../services/fundamentalsData";
import { STOCK_UNIVERSE } from "../data/stocks";

// Build a quick lookup of static fallbacks from the universe
const FALLBACK_MAP = Object.fromEntries(
  STOCK_UNIVERSE.map(s => [s.ticker, s])
);

/** Overlay only non-null values from src onto dst (mutates dst). */
function overlayNonNull(dst, src) {
  if (!src || typeof src !== "object") return dst;
  const out = { ...dst };
  for (const [k, v] of Object.entries(src)) if (v != null) out[k] = v;
  return out;
}

export function useMarketData(holdings) {
  const [liveData,  setLiveData]  = useState({});
  const [loading,   setLoading]   = useState(false);
  const [errors,    setErrors]    = useState([]);
  const [lastFetch, setLastFetch] = useState(null);

  // Track which tickers we've already fetched via Alpha (avoid burning daily quota)
  const fetchedRef     = useRef(new Set());
  // After rate limit / no key, skip Alpha for this session
  const alphaBlockedRef = useRef(false);

  const hasAlpha = true; // Alpha Vantage is server-side via /api/alpha-proxy (uses ALPHA_KEY env var)

  const fetchData = useCallback(async (tickers) => {
    if (!tickers.length) return;
    setLoading(true);

    // ── 1. Alpha Vantage enrichment (skipped if no key, rate limited, or already fetched) ──
    const toFetch = hasAlpha && !alphaBlockedRef.current
      ? tickers.filter(t => !fetchedRef.current.has(t))
      : [];

    if (toFetch.length > 0) {
      setErrors([]);
      try {
        const result = await fetchMultipleStocks(toFetch, FALLBACK_MAP);
        const failed = Object.entries(result)
          .filter(([, v]) => v._error)
          .map(([t]) => t);

        // If rate limited, block Alpha for rest of session
        const rateLimited = Object.values(result).some(v =>
          v._error && (v._error.includes("rate limit") || v._error.includes("daily limit"))
        );
        if (rateLimited) {
          alphaBlockedRef.current = true;
          console.warn("[useMarketData] Alpha Vantage rate/daily limit hit — switching to Yahoo only");
        }

        setErrors(failed);
        setLiveData(prev => ({ ...prev, ...result }));
        toFetch.forEach(t => fetchedRef.current.add(t));
        setLastFetch(new Date());
      } catch (err) {
        console.warn("[useMarketData] Alpha fetch error:", err.message);
        if (err.message.includes("rate limit") || err.message.includes("daily limit")) {
          alphaBlockedRef.current = true;
        }
      }
    }

    // ── 2. Yahoo Finance fundamentals — always runs (3 at a time, 30 min cache) ──
    // This provides live P/E, P/B, EV/EBITDA, beta etc. and overwrites stale static data.
    try {
      const yahooResult = await fetchFundamentalsBatch(tickers);
      if (Object.keys(yahooResult).length > 0) {
        setLiveData(prev => {
          const next = { ...prev };
          for (const [t, data] of Object.entries(yahooResult)) {
            next[t] = overlayNonNull(next[t] ?? {}, data);
          }
          return next;
        });
        setLastFetch(new Date());
      }
    } catch (err) {
      console.warn("[useMarketData] Yahoo fundamentals error:", err.message);
    } finally {
      setLoading(false);
    }
  }, [hasAlpha]);

  // Fetch whenever the set of tickers changes
  useEffect(() => {
    const tickers = holdings.map(h => h.ticker);
    fetchData(tickers);
  }, [holdings, fetchData]);

  // Force-refresh: clear caches and re-fetch everything
  const refresh = useCallback(() => {
    fetchedRef.current      = new Set();
    alphaBlockedRef.current = false;
    clearFundamentalsCache();
    const tickers = holdings.map(h => h.ticker);
    fetchData(tickers);
  }, [holdings, fetchData]);

  // Merge live data on top of static holdings
  const enrichedHoldings = holdings.map(h => ({
    ...h,
    ...(liveData[h.ticker] ?? {}),
    weight: h.weight, // always keep user-set weight
  }));

  return {
    enrichedHoldings,
    loading,
    errors,
    lastFetch,
    hasApiKey: hasAlpha,
    refresh,
  };
}
